import { showUserFriendlyNotyf, __ } from "../../utils/notyf-helpers.js";

/**
 * @file Industry Listing Module
 * @description Handles industry listings and filtering functionality
 */

export class IndustryListing {
  constructor() {
    this.currentFilters = {};
    this.isInitialized = false;
    this.listContainer = null;
    this.filterElements = [];
  }

  /**
   * Initialize the industry listing
   */
  async init() {
    this.listContainer = document.getElementById("industry-list");
    if (!this.listContainer) {
      console.debug("Industry Listing: list container not found, skipping initialization");
      return false;
    }

    console.debug("Industry Listing: Initializing...");

    try {
      this.setupFilters();
      this.setupEventListeners();
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error("Industry Listing: Initialization failed:", error);
      return false;
    }
  }

  /**
   * Setup filter elements
   */
  setupFilters() {
    // Find all filter elements
    this.filterElements = document.querySelectorAll("[data-filter]");

    this.filterElements.forEach((element) => {
      const filterType = element.dataset.filter;
      const filterValue = element.value || element.dataset.value;

      if (filterType && filterValue) {
        this.currentFilters[filterType] = filterValue;
      }
    });
  }

  /**
   * Setup event listeners
   */
  setupEventListeners() {
    // Filter change events
    this.filterElements.forEach((element) => {
      element.addEventListener("change", (e) => {
        this.handleFilterChange(e);
      });
    });

    // Search input events
    const searchInput = document.getElementById("industry-search");
    if (searchInput) {
      searchInput.addEventListener("input", (e) => {
        this.handleSearchInput(e);
      });
    }

    // Sort events
    const sortSelect = document.getElementById("industry-sort");
    if (sortSelect) {
      sortSelect.addEventListener("change", (e) => {
        this.handleSortChange(e);
      });
    }

    // Pagination events
    document.addEventListener("click", (e) => {
      if (e.target.matches(".pagination a")) {
        e.preventDefault();
        this.handlePaginationClick(e);
      }
    });
  }

  /**
   * Handle filter change
   */
  handleFilterChange(event) {
    const element = event.target;
    const filterType = element.dataset.filter;
    const filterValue = element.value;

    if (filterType) {
      this.currentFilters[filterType] = filterValue;
      this.applyFilters();
    }
  }

  /**
   * Handle search input
   */
  handleSearchInput(event) {
    const searchTerm = event.target.value.trim();

    if (searchTerm.length >= 2 || searchTerm.length === 0) {
      this.currentFilters.search = searchTerm;
      this.applyFilters();
    }
  }

  /**
   * Handle sort change
   */
  handleSortChange(event) {
    const sortValue = event.target.value;
    this.currentFilters.sort = sortValue;
    this.applyFilters();
  }

  /**
   * Handle pagination click
   */
  handlePaginationClick(event) {
    const page = event.target.dataset.page;
    if (page) {
      this.currentFilters.page = page;
      this.applyFilters();
    }
  }

  /**
   * Apply current filters
   */
  async applyFilters() {
    try {
      this.showLoadingState();

      const results = await this.fetchFilteredResults();
      this.updateList(results);
    } catch (error) {
      console.error("Industry Listing: Error applying filters:", error);
      this.showErrorState();
    }
  }

  /**
   * Fetch filtered results
   */
  async fetchFilteredResults() {
    const params = new URLSearchParams(this.currentFilters);
    const url = `/wp-json/wp/v2/industry?${params.toString()}`;

    try {
      const response = await fetch(url);
      const data = await response.json();

      return {
        items: data,
        total: parseInt(response.headers.get("X-WP-Total") || 0),
        totalPages: parseInt(response.headers.get("X-WP-TotalPages") || 0),
      };
    } catch (error) {
      console.error("Industry Listing: API request failed:", error);
      throw error;
    }
  }

  /**
   * Update the list with new results
   */
  updateList(results) {
    if (!this.listContainer) return;

    const { items, total, totalPages } = results;

    // Clear existing content
    this.listContainer.innerHTML = "";

    if (!items || items.length === 0) {
      this.showNoResults();
      return;
    }

    // Create items
    items.forEach((item) => {
      const itemElement = this.createListItem(item);
      this.listContainer.appendChild(itemElement);
    });

    // Update pagination
    this.updatePagination(totalPages);

    // Update results count
    this.updateResultsCount(total);
  }

  /**
   * Create list item element
   */
  createListItem(item) {
    const element = document.createElement("div");
    element.className = "industry-item";

    element.innerHTML = `
      <div class="industry-item-content">
        <h3 class="industry-title">
          <a href="${item.link}">${item.title.rendered}</a>
        </h3>
        <div class="industry-meta">
          ${item.meta?.location ? `<span class="location">${item.meta.location}</span>` : ""}
          ${item.meta?.area ? `<span class="area">${item.meta.area}</span>` : ""}
        </div>
        <div class="industry-excerpt">
          ${item.excerpt.rendered}
        </div>
        <div class="industry-actions">
          <a href="${item.link}" class="view-details">View Details</a>
          <button class="add-to-collection" data-id="${item.id}">Add to Collection</button>
        </div>
      </div>
    `;

    // Add event listeners
    const addToCollectionBtn = element.querySelector(".add-to-collection");
    if (addToCollectionBtn) {
      addToCollectionBtn.addEventListener("click", (e) => {
        this.handleAddToCollection(item.id);
      });
    }

    return element;
  }

  /**
   * Handle add to collection
   */
  handleAddToCollection(itemId) {
    // Trigger custom event for collection management
    const event = new CustomEvent("add-to-collection", {
      detail: {
        itemId,
        itemType: "industry",
      },
    });
    document.dispatchEvent(event);
  }

  /**
   * Show loading state
   */
  showLoadingState() {
    if (!this.listContainer) return;

    this.listContainer.innerHTML = `
      <div class="loading-state">
        <div class="spinner"></div>
        <p>Loading industrial parks...</p>
      </div>
    `;
  }

  /**
   * Show no results state
   */
  showNoResults() {
    if (!this.listContainer) return;

    this.listContainer.innerHTML = `
      <div class="no-results">
        <p>No industrial parks found matching your criteria.</p>
        <button class="clear-filters">Clear Filters</button>
      </div>
    `;

    // Add clear filters functionality
    const clearBtn = this.listContainer.querySelector(".clear-filters");
    if (clearBtn) {
      clearBtn.addEventListener("click", () => {
        this.clearFilters();
      });
    }
  }

  /**
   * Show error state
   */
  showErrorState() {
    if (!this.listContainer) return;

    this.listContainer.innerHTML = `
      <div class="error-state">
        <p>An error occurred while loading results.</p>
        <button class="retry-loading">Retry</button>
      </div>
    `;

    // Add retry functionality
    const retryBtn = this.listContainer.querySelector(".retry-loading");
    if (retryBtn) {
      retryBtn.addEventListener("click", () => {
        this.applyFilters();
      });
    }
  }

  /**
   * Update pagination
   */
  updatePagination(totalPages) {
    const paginationContainer = document.getElementById("industry-pagination");
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.innerHTML = "";
      return;
    }

    const currentPage = parseInt(this.currentFilters.page || 1);
    let paginationHTML = '<div class="pagination">';

    // Previous button
    if (currentPage > 1) {
      paginationHTML += `<a href="#" data-page="${currentPage - 1}" class="prev">Previous</a>`;
    }

    // Page numbers
    for (let i = 1; i <= totalPages; i++) {
      if (i === currentPage) {
        paginationHTML += `<span class="current">${i}</span>`;
      } else {
        paginationHTML += `<a href="#" data-page="${i}">${i}</a>`;
      }
    }

    // Next button
    if (currentPage < totalPages) {
      paginationHTML += `<a href="#" data-page="${currentPage + 1}" class="next">Next</a>`;
    }

    paginationHTML += "</div>";
    paginationContainer.innerHTML = paginationHTML;
  }

  /**
   * Update results count
   */
  updateResultsCount(total) {
    const countElement = document.getElementById("results-count");
    if (countElement) {
      countElement.textContent = `${total} industrial park${total !== 1 ? "s" : ""} found`;
    }
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.currentFilters = {};

    // Reset filter elements
    this.filterElements.forEach((element) => {
      if (element.tagName === "SELECT") {
        element.selectedIndex = 0;
      } else if (element.tagName === "INPUT") {
        element.value = "";
      }
    });

    // Reset search
    const searchInput = document.getElementById("industry-search");
    if (searchInput) {
      searchInput.value = "";
    }

    // Reset sort
    const sortSelect = document.getElementById("industry-sort");
    if (sortSelect) {
      sortSelect.selectedIndex = 0;
    }

    this.applyFilters();
  }

  /**
   * Get current filters
   */
  getCurrentFilters() {
    return { ...this.currentFilters };
  }

  /**
   * Set filters programmatically
   */
  setFilters(filters) {
    this.currentFilters = { ...this.currentFilters, ...filters };
    this.applyFilters();
  }

  /**
   * Get listing state
   */
  getListingState() {
    return {
      isInitialized: this.isInitialized,
      currentFilters: this.currentFilters,
      hasContainer: !!this.listContainer,
      filterCount: this.filterElements.length,
    };
  }

  /**
   * Destroy the listing instance
   */
  destroy() {
    // Remove event listeners
    this.filterElements.forEach((element) => {
      element.removeEventListener("change", this.handleFilterChange);
    });

    this.filterElements = [];
    this.listContainer = null;
    this.isInitialized = false;
  }
}

/**
 * Initialize industry listing
 */
export function initIndustryListing() {
  const listing = new IndustryListing();
  return listing.init().then((success) => {
    if (success) {
      // Store instance globally for access
      window.IPHUB_INDUSTRY_LISTING = listing;
    }
    return listing;
  });
}
